import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { CheckCircle, XCircle, Loader2, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewsletterConfirm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [discountCode, setDiscountCode] = useState(null);

    useEffect(() => {
        if (token) {
            confirmSubscription();
        } else {
            setStatus('error');
        }
    }, [token]);

    const confirmSubscription = async () => {
        try {
            const response = await fetch(`${API_URL}/api/newsletter/confirm?token=${token}`);
            
            if (response.ok) {
                // The backend returns HTML, but we'll parse it or redirect
                const text = await response.text();
                
                // Extract discount code from response if present
                const codeMatch = text.match(/class="discount-code">([A-Z0-9-]+)</);
                if (codeMatch) {
                    setDiscountCode(codeMatch[1]);
                }
                
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (error) {
            console.error('Confirmation error:', error);
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Navbar />
            
            <main className="flex-1 flex items-center justify-center p-4 pt-32">
                <div className="max-w-lg w-full text-center">
                    {status === 'loading' && (
                        <div className="space-y-4">
                            <Loader2 className="w-16 h-16 mx-auto animate-spin text-gray-400" />
                            <p className="text-gray-500">Anmeldung wird bestätigt...</p>
                        </div>
                    )}
                    
                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-12 h-12 text-green-600" />
                            </div>
                            
                            <h1 className="text-3xl font-bold">Newsletter-Anmeldung bestätigt!</h1>
                            <p className="text-gray-600">
                                Vielen Dank! Sie erhalten ab jetzt unseren Newsletter mit Events, Angeboten und Neuigkeiten.
                            </p>
                            
                            {discountCode && (
                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-xl mt-6">
                                    <div className="flex justify-center mb-2">
                                        <Gift className="w-10 h-10" />
                                    </div>
                                    <div className="text-xl font-semibold mb-2">Ihr Willkommensgeschenk</div>
                                    <div className="text-sm mb-4">10% Rabatt auf Ihre erste Raumbuchung!</div>
                                    <div className="bg-white text-black py-3 px-6 rounded-lg inline-block">
                                        <span className="text-2xl font-bold tracking-wider">{discountCode}</span>
                                    </div>
                                    <p className="text-sm mt-4 text-green-100">
                                        Der Code wurde auch per E-Mail an Sie gesendet.
                                    </p>
                                </div>
                            )}
                            
                            <div className="bg-gray-50 p-4 rounded-lg text-left text-sm">
                                <strong className="block mb-2">So lösen Sie den Code ein:</strong>
                                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                                    <li>Gehen Sie zur Raumbuchung</li>
                                    <li>Wählen Sie Raum und Termin</li>
                                    <li>Geben Sie den Code im Buchungsformular ein</li>
                                    <li>Der Rabatt wird automatisch berechnet</li>
                                </ol>
                                <p className="text-gray-400 mt-3 text-xs">
                                    * Gilt nur für Raumkosten, nicht für Zusatzoptionen. Einmalig verwendbar.
                                </p>
                            </div>
                            
                            <Button onClick={() => navigate('/buchung')} className="w-full bg-black hover:bg-gray-800">
                                JETZT RAUM BUCHEN →
                            </Button>
                        </div>
                    )}
                    
                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                                <XCircle className="w-12 h-12 text-red-600" />
                            </div>
                            
                            <h1 className="text-3xl font-bold">Link ungültig</h1>
                            <p className="text-gray-600">
                                Der Bestätigungslink ist ungültig oder wurde bereits verwendet.
                            </p>
                            
                            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                                Zur Startseite
                            </Button>
                        </div>
                    )}
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default NewsletterConfirm;
